import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPinIcon } from '@heroicons/react/24/solid';
import config from '@/config';

// Fix Leaflet's default icon issue
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

interface LocationMapProps {
  latitude: number;
  longitude: number;
  title?: string;
  zoom?: number;
  height?: string;
}

const LocationMap: React.FC<LocationMapProps> = ({ 
  latitude, 
  longitude, 
  title = 'Location',
  zoom = 13,
  height = '300px'
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Initialize map
    const map = L.map(mapRef.current).setView([latitude, longitude], zoom);
    mapInstanceRef.current = map;

    // Add OpenStreetMap tiles
    L.tileLayer(config.services.tileServerUrl, {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 18,
    }).addTo(map);

    // Add marker
    const marker = L.marker([latitude, longitude]).addTo(map);
    marker.bindPopup(title).openPopup();

    // Add circle to show approximate area
    L.circle([latitude, longitude], {
      color: 'blue',
      fillColor: '#30f',
      fillOpacity: 0.1,
      radius: 500 // 500 meters
    }).addTo(map);

    // The container is often hidden when this mounts — TabPanel keeps inactive
    // tabs mounted with display:none, so Leaflet measures it at 0 and only
    // loads the corner tiles. Recompute size whenever the box changes,
    // including the hidden→visible transition when the tab is selected.
    const resizeObserver = new ResizeObserver(() => {
      map.invalidateSize();
    });
    resizeObserver.observe(mapRef.current);

    return () => {
      resizeObserver.disconnect();
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Update map when coordinates change
  useEffect(() => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setView([latitude, longitude], zoom);
      
      // Clear existing layers
      mapInstanceRef.current.eachLayer((layer) => {
        if (layer instanceof L.Marker || layer instanceof L.Circle) {
          mapInstanceRef.current!.removeLayer(layer);
        }
      });

      // Add new marker and circle
      const marker = L.marker([latitude, longitude]).addTo(mapInstanceRef.current);
      marker.bindPopup(title).openPopup();

      L.circle([latitude, longitude], {
        color: 'blue',
        fillColor: '#30f',
        fillOpacity: 0.1,
        radius: 500
      }).addTo(mapInstanceRef.current);
    }
  }, [latitude, longitude, title, zoom]);

  return (
    <div className="relative">
      <div 
        ref={mapRef} 
        style={{ height, width: '100%' }}
        className="rounded-lg overflow-hidden border border-gray-300"
      />
      <div className="absolute top-2 right-2 bg-white px-2 py-1 rounded shadow-md flex items-center gap-1">
        <MapPinIcon className="h-4 w-4 text-red-600" />
        <span className="text-xs font-medium">
          {latitude.toFixed(6)}, {longitude.toFixed(6)}
        </span>
      </div>
    </div>
  );
};

export default LocationMap;